import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gexService } from '../../services/gexService';
import { fetchGammaByExpiry, num, netGex, daysToExpiry } from '../../services/gammaService';
import { fmtUsDate } from './format';
import { EXPLORER_CACHE } from './cachePolicy';
import { is404 } from './useDailyBars';

/**
 * The two gamma tables under the Gamma tab's charts: exposure by strike, and
 * exposure by expiration.
 *
 * The charts show shape; these give the numbers. Both are real data.
 *
 * ⚠️ The two tables read from **different services**, which is deliberate and
 * temporary. By-expiry is on QuantForge's `/v1/gex-by-expiry`; by-strike is
 * still on the portal's by-strike route because it shares a React Query cache
 * entry with the all-expiries chart beside it, and moving one without the other
 * would double the requests. Both move when QF's by-strike split is live.
 *
 * ⚠️ **The two do not reconcile, and that is intended.** `/v1/gex-by-expiry`
 * is unclamped; the by-strike path keeps a ±5% moneyness band. So summing this
 * table's Net GEX will exceed summing the one beside it, because a per-expiry
 * total must not silently drop far strikes. Do not "fix" one to match the
 * other.
 *
 * Note that every numeric field from both feeds may arrive as a STRING. Coerce
 * before doing anything but arithmetic — calling a number method on one throws
 * at runtime while typechecking cleanly where the types declare `number`.
 */

/** Compact signed exposure: 1.24B, −840M, 3.1K. */
function fmtExposure(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const body =
    abs >= 1e9
      ? `${(abs / 1e9).toFixed(2)}B`
      : abs >= 1e6
        ? `${(abs / 1e6).toFixed(1)}M`
        : abs >= 1e3
          ? `${(abs / 1e3).toFixed(1)}K`
          : abs.toFixed(0);
  return `${n < 0 ? '−' : ''}${body}`;
}

function netTone(n: number): string {
  return n > 0 ? 'text-bull-blue' : n < 0 ? 'text-bear-red' : 'text-gray-400';
}

const HEAD = 'px-3 py-2 font-semibold text-gray-400 whitespace-nowrap text-center';
const CELL = 'px-3 py-1.5 whitespace-nowrap text-center';

/* ── sorting ────────────────────────────────────────────────────────── */

type SortDir = 'asc' | 'desc';
interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/**
 * Clickable column header.
 *
 * The arrow only shows on the active column — a permanent indicator on every
 * header reads as a control that has not been used yet, rather than as the
 * state of the sort.
 */
function SortHeader<K extends string>({ label, sortKey, sort, onSort }: {
  label: string;
  sortKey: K;
  sort: SortState<K>;
  onSort: (key: K) => void;
}): React.ReactElement {
  const active = sort.key === sortKey;
  return (
    <th className={`${HEAD} p-0`}>
      <button
        onClick={() => onSort(sortKey)}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`w-full px-3 py-2 inline-flex items-center justify-center gap-1 transition-colors hover:text-white ${
          active ? 'text-accent-yellow' : ''
        }`}
      >
        {label}
        <span className={active ? '' : 'invisible'}>{sort.dir === 'asc' ? '▲' : '▼'}</span>
      </button>
    </th>
  );
}

/**
 * Sorts a copy by the given accessor.
 *
 * Strings compare with localeCompare so ISO dates order correctly; everything
 * else is numeric.
 */
function sortRows<T>(rows: T[], get: (row: T) => number | string, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (typeof av === 'string' || typeof bv === 'string') {
      return sign * String(av).localeCompare(String(bv));
    }
    return sign * (av - bv);
  });
}

/** Click the active column to flip it; a new column starts on its natural
    direction — biggest-first for numbers, earliest-first for dates. */
function nextSort<K extends string>(prev: SortState<K>, key: K, naturalDesc: boolean): SortState<K> {
  if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: naturalDesc ? 'desc' : 'asc' };
}

function Panel({ title, meta, children }: {
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="p-4 bg-dark-surface">
      <div className="flex items-baseline gap-3 pb-2 mb-3 flex-wrap border-b border-dark-border">
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">{title}</h2>
        {meta}
      </div>
      {children}
    </section>
  );
}

function Note({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-gray-300 text-sm">{children}</p>;
}

/* ── by strike ──────────────────────────────────────────────────────── */

type StrikeKey = 'strike' | 'call' | 'put' | 'net';
const STRIKE_COLS: [StrikeKey, string][] = [
  ['strike', 'Strike'],
  ['call', 'Call GEX'],
  ['put', 'Put GEX'],
  ['net', 'Net GEX'],
];

function ByStrike({ symbol }: { symbol: string }): React.ReactElement {
  // Same query key the chart's all-expiries panel uses, so this is a cache
  // hit rather than a second request for the same data.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gexByStrike', symbol, false, null],
    queryFn: () => gexService.fetchGexByStrike(symbol),
    // A symbol outside the dataset 404s every time, so retrying only delays
    // the empty state. Same rule the price chart uses.
    retry: (count, err) => !is404(err) && count < 2,
    ...EXPLORER_CACHE,
    enabled: !!symbol,
  });

  // ⚠️ Coverage is partial, and a gap is NOT a fault. SMH and other symbols
  // outside the gamma dataset 404 here, and reporting that in red reads as
  // the page being broken rather than as the provider not publishing this
  // one. Anything that is not a 404 still surfaces as an error, because that
  // genuinely is one.
  const notCovered = isError && is404(error);

  // Default: highest strike first, so it opens as a price ladder like the
  // chart beside it.
  const [sort, setSort] = useState<SortState<StrikeKey>>({ key: 'strike', dir: 'desc' });

  const rows = useMemo(() => {
    const all = (data ?? [])
      .map((d) => {
        const call = Number(d.call_gex) || 0;
        const put = Number(d.put_gex) || 0;
        return { strike: Number(d.strike), call, put, net: call + put };
      })
      .filter((r) => Number.isFinite(r.strike));
    return sortRows(all, (r) => r[sort.key], sort.dir);
  }, [data, sort]);

  return (
    <Panel
      title="Gamma Exposure by Strike"
      meta={
        !!rows.length && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            <span className="font-numeric">{rows.length}</span> strikes
          </span>
        )
      }
    >
      {isLoading ? (
        <Note>Loading&hellip;</Note>
      ) : notCovered ? (
        <Note>No gamma exposure published for {symbol}</Note>
      ) : isError ? (
        <p className="text-bear-red text-sm">Failed to load gamma by strike</p>
      ) : !rows.length ? (
        <Note>No gamma exposure published for {symbol}</Note>
      ) : (
        <div className="rounded border border-dark-border overflow-hidden">
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
                <tr>
                  {STRIKE_COLS.map(([key, label]) => (
                    <SortHeader
                      key={key}
                      label={label}
                      sortKey={key}
                      sort={sort}
                      onSort={(k) => setSort((prev) => nextSort(prev, k, true))}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  return (
                    <tr
                      key={r.strike}
                      className="border-b border-dark-border/40 hover:bg-dark-surface-light transition-colors"
                    >
                      <td className={`${CELL} font-numeric text-white`}>
                        {r.strike.toFixed(2)}
                      </td>
                      <td className={`${CELL} font-numeric text-bull-blue`}>{fmtExposure(r.call)}</td>
                      <td className={`${CELL} font-numeric text-bear-red`}>{fmtExposure(r.put)}</td>
                      <td className={`${CELL} font-numeric font-semibold ${netTone(r.net)}`}>
                        {fmtExposure(r.net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ── by expiration ──────────────────────────────────────────────────── */

type ExpiryKey = 'expiry' | 'dte' | 'call' | 'put' | 'net';
const EXPIRY_COLS: [ExpiryKey, string][] = [
  ['expiry', 'Expiry'],
  ['dte', 'DTE'],
  ['call', 'Call GEX'],
  ['put', 'Put GEX'],
  ['net', 'Net GEX'],
];

function ByExpiration({ symbol }: { symbol: string }): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['qfGammaByExpiry', symbol],
    queryFn: () => fetchGammaByExpiry(symbol),
    ...EXPLORER_CACHE,
    enabled: !!symbol,
    retry: false,
  });

  // Default: soonest expiry first — the near-dated chain is what moves.
  const [sort, setSort] = useState<SortState<ExpiryKey>>({ key: 'expiry', dir: 'asc' });

  const rows = useMemo(() => {
    // ⚠️ `expiries`, not `rows` — QF's key. Reading the wrong one yields [],
    // which renders as "no data" on a perfectly good 200.
    const all = (data?.expiries ?? []).map((d) => ({
      expiry: d.expiry,
      settlement: d.settlement,
      root: d.root,
      // Computed here: QF sends the expiry date, not a day count.
      dte: daysToExpiry(d.expiry),
      call: num(d.call_gex),
      put: num(d.put_gex),
      // ⚠️ Addition — puts arrive already negative. See `netGex`.
      net: netGex(d),
    }));
    return sortRows(all, (r) => r[sort.key], sort.dir);
  }, [data, sort]);

  /**
   * Dates carrying two settlement regimes, so the label can disambiguate only
   * where it matters.
   *
   * ⚠️ An index monthly lists an AM- and a PM-settled chain on the same third
   * Friday. They are different products and must never be summed: QF measured
   * 2026-08-21 at SPX 3,258,872 against SPXW 13,374, so a fused row would read
   * 244x the PM book and be true of neither.
   */
  const sharedDates = useMemo(() => {
    const seen = new Map<string, number>();
    rows.forEach((r) => seen.set(r.expiry, (seen.get(r.expiry) ?? 0) + 1));
    return new Set([...seen].filter(([, n]) => n > 1).map(([d]) => d));
  }, [rows]);

  return (
    <Panel
      title="Gamma Exposure by Expiration"
      meta={
        (data?.date || rows.length > 0) && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            {data?.date && (
              <span className="font-numeric">{fmtUsDate(data.date)}</span>
            )}
            {data?.date && rows.length > 0 ? ' · ' : null}
            {rows.length > 0 && (
              <>
                <span className="font-numeric">{rows.length}</span> expiries
              </>
            )}
          </span>
        )
      }
    >
      {isLoading ? (
        <Note>Loading&hellip;</Note>
      ) : !rows.length ? (
        <Note>No gamma exposure by expiration available for {symbol}</Note>
      ) : (
        <div className="rounded border border-dark-border overflow-hidden">
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
                <tr>
                  {EXPIRY_COLS.map(([key, label]) => (
                    <SortHeader
                      key={key}
                      label={label}
                      sortKey={key}
                      sort={sort}
                      // Dates and DTE read soonest-first; exposures biggest-first.
                      onSort={(k) => setSort((prev) => nextSort(prev, k, k !== 'expiry' && k !== 'dte'))}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    // ⚠️ Keyed on (expiry, root): a monthly date carries two
                    // rows, so the date alone is not unique.
                    key={`${r.expiry}:${r.root}`}
                    className={`border-b border-dark-border/40 hover:bg-dark-surface-light transition-colors ${
                      r.dte === 0 ? 'bg-accent-yellow/10' : ''
                    }`}
                  >
                    <td
                      className={`${CELL} font-numeric ${
                        r.dte === 0 ? 'text-accent-yellow font-semibold' : 'text-white'
                      }`}
                    >
                      {fmtUsDate(r.expiry)}
                      {/* Shown only where a date really does carry both books,
                          so ordinary weeklies stay uncluttered. Without it the
                          two rows look like a duplicate row rather than two
                          different products. */}
                      {sharedDates.has(r.expiry) && (
                        <span className="ml-1 text-[10px] text-gray-400">{r.settlement}</span>
                      )}
                    </td>
                    <td className={`${CELL} font-numeric text-gray-200`}>
                      {r.dte === 0 ? '0DTE' : `${r.dte}d`}
                    </td>
                    <td className={`${CELL} font-numeric text-bull-blue`}>{fmtExposure(r.call)}</td>
                    <td className={`${CELL} font-numeric text-bear-red`}>{fmtExposure(r.put)}</td>
                    <td className={`${CELL} font-numeric font-semibold ${netTone(r.net)}`}>
                      {fmtExposure(r.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ── composition ────────────────────────────────────────────────────── */

export function GammaTables({ symbol }: { symbol: string }): React.ReactElement {
  // One card: both tables read the same greek-exposure snapshot, sliced two
  // ways. The 1px gap over a border-coloured background draws the divider and
  // keeps drawing it correctly when they stack.
  return (
    <div className="rounded-lg border border-dark-border bg-dark-border overflow-hidden grid grid-cols-1 xl:grid-cols-2 gap-px">
      <ByStrike symbol={symbol} />
      <ByExpiration symbol={symbol} />
    </div>
  );
}
